import { ENTERPRISE_PARTNER_ID, GROUP_STATUS, LEAD_TYPE } from '@brighte-enterprise/common/constants';

import { UserRole } from '@app-types/User';
import { EnterprisePartner } from '@typeDefs/groups';

export enum FINANCIAL_PRODUCT_SLUG {
  Electrify2515 = 'electrify-2515',
}

export const states: Record<string, string> = {
  ACT: 'Australian Capital Territory',
  NSW: 'New South Wales',
  NT: 'Northern Territory',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  VIC: 'Victoria',
  WA: 'Western Australia',
};

export const showContactRoles = [UserRole.AdminOperations, UserRole.EnterpriseConcierge, UserRole.EnterprisePartnerPII];

export enum ALERT_LEVEL {
  Warning = 'warning',
  Critical = 'critical',
}

export const pipelineAlertThresholds: Record<ALERT_LEVEL, number> = {
  [ALERT_LEVEL.Critical]: 15,
  [ALERT_LEVEL.Warning]: 10,
};

export const newStatusStaleDays = 10;

export const allowedRolesForEnterprisePartners = [
  UserRole.Team,
  UserRole.Vendor,
  UserRole.Manager,
  UserRole.AdminOperations,
  UserRole.Admin,
  UserRole.SuperAdmin,
  UserRole.EnterpriseConcierge,
  UserRole.Service,
];

// Backward compatibility alias
export const allowedRolesForEnterpriseGroups = allowedRolesForEnterprisePartners;

export const allowedRolesForDocuments = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

export const VALID_FILE_EXTENSIONS = {
  IMAGES: ['jpg', 'jpeg', 'png'],
  DOCUMENTS: ['pdf'],
  SPREADSHEETS: ['csv'],
  ALL: ['jpg', 'jpeg', 'png', 'pdf', 'csv'],
};

export const internalRoles = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

// ── Shared pre-marketplace config fragments ───────────────────────────────────
// Used for both channel-level fields and backward-compat partner-level fields.

const SOLAR_BUNDLE_SUBCATEGORIES = {
  'solar-battery-ev-charger': ['solar-system', 'battery-storage', 'ev-charger'],
};

const COMMBANK_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'household', required: true },
  { id: 'upgrade-profile', required: true },
  { id: 'financing-interest', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const NSW_HESL_STEP_FLOW = [
  { id: 'eligibility-check', required: true },
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const STANDARD_BUNDLE_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const OVO_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'bundle-categories', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

export const enterpriseGroups: EnterprisePartner[] = [
  {
    id: ENTERPRISE_PARTNER_ID.evee,
    name: 'Evee',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.tas,
    name: 'TAS Government',
    type: LEAD_TYPE.Marketplace,
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.oneMillionWomen,
    name: 'One Million Women',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.commbank,
    name: 'CommBank',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'commbank',
          financialProductId: 'commbank-residential',
          title: 'Home Energy Upgrades',
          showTitleInMarketplace: false,
          preMarketplace: {
            stepFlow: COMMBANK_STEP_FLOW,
            stepConfiguration: {
              categories: { multiSelection: true, options: [] },
              recommendedVendors: { selectVendors: false, pickVendorsInMarketplace: true },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      hasCoBrandedLogo: true,
      sparkThemeName: 'pantheon',
      backgroundColour: '#F4F4F4',
      cardBorderRadius: 'none',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.commbank.com.au',
      brighteSiteSlug: 'commbank',
      contactNumber: '1300 500 107',
      marketplaceFlag: 'commbank_residential',
      leadPricing: 35,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.racq,
    name: 'RACQ',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'racq',
          financialProductId: 'brighte-pay',
          title: 'Home Upgrade Program',
          showTitleInMarketplace: true,
          preMarketplace: {
            stepFlow: STANDARD_BUNDLE_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: ['solar-battery-ev-charger', 'hot-water-heat-pumps-hwhp', 'water-filtration'],
              },
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
                sortingPreference: 'RANDOM',
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      logoWidth: 90,
      sparkThemeName: 'bravo',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      customerIdRequirement: 'optional',
      hasMarketingOptIn: true,
      hasCustomAssetPath: true,
      vendorBrowsingEnabled: false,
      filterVendorByPartner: true,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      brighteSiteSlug: 'racq',
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_racq',
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.ovoEnergy,
    name: 'OVO Energy',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'ovo-energy',
          financialProductId: 'brighte-pay',
          showTitleInMarketplace: false,
          preMarketplace: {
            stepFlow: OVO_STEP_FLOW,
            stepConfiguration: {
              // No `categories` block — OVO_STEP_FLOW has no `categories` step (goes straight to `bundle-categories`).
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
                sortingPreference: 'RANDOM',
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: true,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_ovo',
      leadPricing: 45,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.rewiringAustralia,
    name: 'Rewiring Australia',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: true },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'electrify-2515',
          financialProductId: 'electrify-2515',
          fixedPostcode: '2515',
          title: 'Electrify 2515 Community Pilot',
          showTitleInMarketplace: true,
          leadLabel: 'Electrify 2515 Community Pilot',
          vendorGuidelineUrl: 'https://brighte.com.au/electrify-2515-vendor-guide',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: false,
      multiVendor: true,
      intake: true,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.electrify2515.org/pilot',
      brighteSiteSlug: 'electrify-2515',
      contactNumber: '1300 135 100',
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.act,
    name: 'ACT Government',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'act',
          financialProductId: 'act-shs-loan',
          title: 'ACT Sustainable Household Scheme',
          showTitleInMarketplace: true,
        },
        {
          slug: 'act-community',
          financialProductId: 'act-community-loan',
          title: 'ACT Community Household Scheme',
          showTitleInMarketplace: true,
        },
        {
          slug: 'act-sfa',
          financialProductId: 'act-solar-for-apartments-loan',
          title: 'ACT Solar for Apartments',
          showTitleInMarketplace: true,
        },
        // Product ID uses the original internal name (SLIP = Solar for Low Income Programme); renamed to HESP before launch but ID was not changed.
        {
          slug: 'act-hesp',
          financialProductId: 'act-slip-loan',
          title: 'ACT Home Energy Support Program',
          showTitleInMarketplace: true,
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: true,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.brighte,
    name: 'Brighte',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'brighte',
          showTitleInMarketplace: false,
        },
        {
          slug: 'nsw-home-energy-saver',
          financialProductId: 'brighte-home-energy-saver-loan',
          title: 'NSW Home Energy Saver Loan',
          showTitleInMarketplace: true,
          leadLabel: 'Brighte Home Energy Saver Loan',
          vendorGuidelineUrl: 'https://brighte.com.au/support/partners/nsw-home-energy-saver-loan',
          vendorEmail: {
            leadReceivedPanel: {
              heading: 'About the loan',
              body: 'NSW Government’s Home Energy Saver Program is here — offering eligible households up to $15,000 at 0% interest for solar, batteries, heating and cooling, and more.',
              ctaLabel: 'Learn more',
            },
          },
          preMarketplace: {
            stepFlow: NSW_HESL_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: [
                  'solar-battery-system',
                  'hot-water-heat-pumps-hwhp',
                  'electric-heating-cooling-systems-reverse-cycle',
                  'ev-charging-infrastructure',
                ],
              },
              bundleCategories: {
                enabled: true,
                subcategories: { 'solar-battery-system': ['solar-system', 'battery-storage'] },
              },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: true,
                sortingPreference: 'RANKING',
              },
              introduction: {
                stepGuideLink: 'https://brighte.com.au/homeowners/nsw-home-energy-saver-loan/next-steps',
              },
              eligibilityCheck: {
                items: [
                  'Your installation address must be in NSW',
                  "Your household's total taxable income must be under $210,000",
                ],
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
    },
  },
];
